<script setup>
// Lead Workspace — Notes tab. Per the reviewed decision, there is no
// dedicated Nexceo note model — this reuses Chatwoot's own private
// conversation notes (Message with private: true) on the lead's linked
// conversation, the same mechanism Nexceo::NotificationService's
// create_private_note_with_routing already writes to. Leads without a
// linked conversation show an honest empty state — note categories/manual
// per-note tagging shown in the reference have no backing data model here,
// so only a plain searchable chronological list + quick-add is offered;
// "Recent Tags" reuses the same conversation labels the Overview tab's Tags
// card manages (real data, not per-note).
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import ConversationApi from 'dashboard/api/conversations';
import { formatDateTime } from '../leadFormatters';

const props = defineProps({
  lead: { type: Object, required: true },
});

const notes = ref([]);
const isLoading = ref(true);
const draft = ref('');
const isSaving = ref(false);
const search = ref('');
const tags = ref([]);

async function load() {
  if (!props.lead.conversation_id) {
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await MessageApi.getPreviousMessages({
      conversationId: props.lead.conversation_id,
      after: 0,
      before: Number.MAX_SAFE_INTEGER,
    });
    const list = Array.isArray(data) ? data : (data?.payload ?? []);
    notes.value = list
      .filter(m => m.private)
      .sort((a, b) => b.created_at - a.created_at);
  } catch {
    notes.value = [];
  } finally {
    isLoading.value = false;
  }
}

async function loadTags() {
  if (!props.lead.conversation_id) return;
  try {
    const { data } = await ConversationApi.getLabels(props.lead.conversation_id);
    tags.value = data?.payload ?? [];
  } catch {
    tags.value = [];
  }
}

onMounted(() => {
  load();
  loadTags();
});

const filteredNotes = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return notes.value;
  return notes.value.filter(n => (n.content || '').toLowerCase().includes(q));
});

async function addNote() {
  const content = draft.value.trim();
  if (!content || !props.lead.conversation_id) return;
  isSaving.value = true;
  try {
    await MessageApi.create({
      conversationId: props.lead.conversation_id,
      message: content,
      private: true,
    });
    draft.value = '';
    useAlert('Note added.');
    load();
  } catch {
    useAlert('Failed to add note.');
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4 p-4">
    <div class="rounded-xl border border-n-weak bg-n-background p-4">
      <div class="flex items-center justify-between mb-3 gap-3">
        <h3 class="text-sm font-semibold text-n-slate-12 shrink-0">
          All Notes<template v-if="notes.length"> ({{ notes.length }})</template>
        </h3>
        <div v-if="notes.length" class="relative w-48">
          <span class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-7" />
          <input
            v-model="search"
            type="text"
            placeholder="Search notes…"
            class="!border !border-solid !border-n-weak rounded-lg !pl-8 pr-2 py-1.5 text-xs w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500"
          />
        </div>
      </div>

      <div v-if="!lead.conversation_id" class="text-sm text-n-slate-8">
        Notes require a linked conversation — this lead doesn't have one yet.
      </div>
      <div v-else-if="isLoading" class="text-sm text-n-slate-8">Loading…</div>
      <div v-else-if="!notes.length" class="text-sm text-n-slate-8">No notes yet.</div>
      <p v-else-if="!filteredNotes.length" class="text-sm text-n-slate-8">No notes match "{{ search }}".</p>
      <ul v-else class="space-y-3">
        <li
          v-for="n in filteredNotes"
          :key="n.id"
          class="rounded-lg bg-n-alpha-1 p-3"
        >
          <p class="text-sm text-n-slate-12 whitespace-pre-wrap">{{ n.content }}</p>
          <p class="text-xs text-n-slate-8 mt-1.5 flex items-center gap-1.5">
            <span class="i-lucide-user size-3" />
            {{ n.sender?.name || 'Team' }}
            <span>· {{ formatDateTime(n.created_at * 1000) }}</span>
          </p>
        </li>
      </ul>
    </div>

    <div class="flex flex-col gap-4">
      <div class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Quick Note</h3>
        <textarea
          v-model="draft"
          rows="4"
          :disabled="!lead.conversation_id"
          placeholder="Type your note…"
          class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500 resize-none disabled:opacity-50"
        />
        <button
          :disabled="isSaving || !draft.trim() || !lead.conversation_id"
          class="mt-2 w-full py-2 text-sm font-medium bg-woot-500 text-white rounded-lg disabled:opacity-50"
          @click="addNote"
        >
          {{ isSaving ? 'Saving…' : 'Save Note' }}
        </button>
      </div>

      <div v-if="lead.conversation_id" class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Recent Tags</h3>
        <div v-if="!tags.length" class="text-xs text-n-slate-8">No tags yet.</div>
        <div v-else class="flex flex-wrap gap-1.5">
          <span
            v-for="tag in tags"
            :key="tag"
            class="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400"
          >
            {{ tag }}
          </span>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-1">Note Visibility</h3>
        <p class="text-xs text-n-slate-8">
          Notes are private team notes — never visible to the customer.
        </p>
      </div>
    </div>
  </div>
</template>
